import { useState, useEffect } from 'react';
import Table from '../../components/Table';
import { Grid, IconButton, Tooltip } from '@mui/material';
import Text from 'components/Text';
import DialogBox from '../../components/DialogBox';
import EmailIcon from '@mui/icons-material/Email';
import UpdateUser from './UpdateUser';
import Loader from 'components/Loader';
import { style } from 'constant/constants';
import AddIcon from '@mui/icons-material/Add';
import SuccessError from 'components/SuccessError';
import CustomButton from 'components/Button';
import { useSelector, useDispatch } from 'react-redux';
import { authPostRequest, getAuthRequest } from 'Api/api';
import { ROLES } from 'constant/enums';
import { isSuperAdmin, tableOptionFilter, isAdmin, isUser, isAreaAdmin } from 'utils/utils';
import CreateUser from './CreateUser';
import DeleteSpan from 'components/DeleteSpan';
import { addUsers } from 'reduxStore/authSlice';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import TextEllipsize from 'utils/TextEllipsize';
import { useNavigate } from 'react-router-dom';
import { dateFormatterUTC } from 'utils/dateFormatter';
import FilterDropdown from 'components/filterDropdown';

const columnOptions = {
  setCellProps: () => ({ style: { maxWidth: '200px' } }),
  customBodyRender: (value) => <TextEllipsize value={value} length={25} />
};

const filterOptions = [
  {
    name: 'Current Users',
    value: 'Current Store Users'
  },
  {
    name: 'All Store Users',
    value: 'All Store Users'
  }
];

const Users = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [addOpenModal, setAddOpenModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showAlertBar, setShowAlertBar] = useState(false);
  const [isFormSaved, setIsFormSaved] = useState(false);
  const [successMessage, setSuccessMessage] = useState([]);
  const [isDeleted, setIsDeleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);
  const [user, setUser] = useState();
  const [users, setUsers] = useState();
  const [filterBy, setFilterBy] = useState('Current Store Users');

  const { userData, storeData, adminAccess, roleBeforeAccess } = useSelector((store) => store.auth);

  const isOriginalSuperAdmin = () => {
    return (
      isSuperAdmin(userData?.roles) ||
      (adminAccess && roleBeforeAccess && ROLES[roleBeforeAccess] === ROLES.ROLE_SUPER_ADMIN)
    );
  };

  const isOriginalAreaAdmin = () => {
    return (
      isAreaAdmin(userData?.roles) ||
      (adminAccess && roleBeforeAccess && ROLES[roleBeforeAccess] === ROLES.ROLE_AREA_ADMIN)
    );
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();

  //For Open Dialog for delete user
  const handleDialogOpen = (userId) => {
    const u = users?.find((u) => u?.id == userId);
    setUser(u);
    setOpenDialog(true);
  };

  //For Open modal for update user
  const handleOpenModal = (userId) => {
    const u = users?.find((u) => u?.id == userId);
    setUser(u);
    setOpenModal(true);
  };

  //Handle confirmation for delete
  const handleConfirm = async () => {
    try {
      const params = {
        userId: userData.userId,
        email: user.email,
        id: userData.userId
      };
      setShowLoader(true);
      setShowAlertBar(false);
      setIsDeleted(false);
      const delUser = await authPostRequest('deleteUser', params);

      if (delUser.resStatus === 1) {
        setSuccessMessage([delUser.resMessage]);
        window.scrollTo(0, 0);
        setIsDeleted(true);
        setShowLoader(false);
      } else {
        setErrorMessage([delUser.resMessage]);
        window.scrollTo(0, 0);
        setShowLoader(false);
      }
      setIsFormSaved(true);

      setShowAlertBar(true);
      setOpenDialog(!openDialog);
    } catch (err) {
      setErrorMessage(['Error']);
      window.scrollTo(0, 0);
      setShowLoader(false);
    }
  };

  //state set from delete user
  const handleDialogState = () => {
    setOpenDialog(!openDialog);
  };

  // filter for admin access
  const filterUsersList = (list) => {
    return (
      list
        .filter(
          (u) => ROLES[u.role] !== ROLES.ROLE_AREA_ADMIN && ROLES[u.role] !== ROLES.ROLE_SUPER_ADMIN
        )
        // Exclude users whose associated store(s) are inactive
        .filter((u) => {
          const stores = u?.storesList ?? [];
          // If no store info, don't keep user; if present, require at least one active store
          return stores.length === 0 ? false : stores.some((s) => s?.active);
        })
    );
  };

  const getCurrentStoreUsers = async () => {
    try {
      const requestParams = {
        storeId: adminAccess
          ? storeData?.id
          : isAdmin(userData?.roles)
            ? userData?.storesList[0]?.storeId
            : null
      };
      if (
        !requestParams?.storeId ||
        requestParams?.storeId == null ||
        requestParams?.storeId == undefined ||
        isOriginalAreaAdmin()
      ) {
        console.log('users');
        navigate('/Stores');
      }
      if (requestParams && isUser(userData?.roles)) {
        navigate('/403');
      }
      const usersList = await authPostRequest('getStoreUsers', requestParams);
      return filterUsersList(usersList?.usersResponses);
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getAllUsers = async () => {
    try {
      const usersList = await getAuthRequest('getUsersList');
      return filterUsersList(usersList?.usersResponses);
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getFilteredUsers = () => {
    if (!users) return [];
    if (filterBy === 'Current Store Users') {
      const selectedStoreId =
        (adminAccess ? storeData?.id : userData?.storesList?.[0]?.storeId) ?? userData?.storeId;
      if (!selectedStoreId) {
        return [];
      }
      return users.filter((u) => {
        const userStoreId = u?.storesList?.[0]?.storeId ?? u?.storeId;
        const matchesStore =
          userStoreId == selectedStoreId ||
          (u?.storesList ?? []).some((s) => s?.storeId == selectedStoreId);
        if (!matchesStore) return false;

        const targetStore = (u?.storesList ?? []).find((s) => s?.storeId == selectedStoreId);
        // If we found the matching store, require it to be active. If not present, keep user.
        return targetStore ? !!targetStore.active : true;
      });
    }
    return users;
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      setShowLoader(true);
      try {
        const list =
          isOriginalAreaAdmin() || isOriginalSuperAdmin()
            ? await getAllUsers()
            : await getCurrentStoreUsers();
        if (active) setUsers(list);
      } catch (err) {
        console.error(err);
        if (active) setUsers([]);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [isFormSaved, isDeleted, adminAccess, roleBeforeAccess, userData?.roles]);

  useEffect(() => {
    if (users) {
      dispatch(addUsers(users));
      getFilteredUsers();
      setShowLoader(false);
    }
    return () => {
      console.log('unmount');
    };
  }, [users]);

  const columns = [
    {
      name: 'id',
      label: 'ID',
      options: {
        display: 'excluded',
        filter: false,
        sort: false
      }
    },

    {
      name: 'firstName',
      label: 'First Name',
      options: {
        filter: true,
        sort: false,
        ...columnOptions
      }
    },

    {
      name: 'lastName',
      label: 'Last Name',
      options: {
        filter: true,
        sort: false,
        ...columnOptions
      }
    },

    {
      name: 'email',
      label: 'Email',
      options: {
        filter: true,
        sort: false,
        ...columnOptions
      }
    },

    {
      name: 'lastLogin',
      label: 'Last Login',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => dateFormatterUTC(value)
      }
    },

    {
      name: 'store',
      label: 'Store',
      options: {
        filter: true,
        sort: false,
        ...columnOptions,
        customBodyRender: (value, tableMeta) => {
          const id = tableMeta.rowData?.[0];
          const u = users?.find((u) => u?.id == id);
          const name = u?.storesList?.[0]?.storeName ?? u?.storeName ?? '';
          return <TextEllipsize value={name} length={25} />;
        }
      }
    },

    {
      name: 'role',
      label: 'Role',
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value) => ROLES[value]
      }
    },

    {
      name: 'actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const id = tableMeta.rowData?.[0];
          return (
            <Grid sx={{ ml: '-1.5vh' }}>
              {id != userData?.userId && (
                <>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => handleDialogOpen(id)}>
                      <DeleteForeverIcon style={{ color: '#800000' }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              <Tooltip title="Update">
                <IconButton onClick={() => handleOpenModal(id)}>
                  <EditIcon style={{ color: '#040273' }} />
                </IconButton>
              </Tooltip>

              {!users.find((u) => u?.id == id)?.active && (
                <Tooltip title="Resend invite">
                  <IconButton onClick={() => sendInvite(id)}>
                    <EmailIcon style={{ color: '#040273' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          );
        }
      }
    }
  ];

  // Keep a stable column definition and only toggle visibility of the 'store' column.
  // This avoids internal table filter state getting misaligned when columns length/order changes.
  const computedColumns = columns.map((col) => {
    if (col.name !== 'store') return col;
    return {
      ...col,
      options: {
        ...col.options,
        display: filterBy === 'Current Store Users' ? 'excluded' : 'true',
        // When the store column is hidden, also disable its filter/search UI
        filter: filterBy === 'Current Store Users' ? false : col.options?.filter ?? true,
        searchable: filterBy === 'Current Store Users' ? false : col.options?.searchable ?? true,
        // Clear any lingering filter selections if the column becomes hidden
        filterList: filterBy === 'Current Store Users' ? [] : col.options?.filterList ?? []
      }
    };
  });

  const sendInvite = async (userId) => {
    const user = users?.find((u) => u?.id == userId);

    try {
      const requestParams = {
        userId: userData.userId,
        email: user.email
      };
      setShowLoader(true);
      setShowAlertBar(false);
      const invite = await authPostRequest('resendInvite', requestParams);

      setShowLoader(false);

      if (invite.resStatus === 1) {
        setSuccessMessage([invite.resMessage]);
      } else {
        setErrorMessage([invite.resMessage]);
      }
      setShowAlertBar(true);
      window.scrollTo(0, 0);
    } catch (err) {
      return err;
    }
  };

  return (
    <>
      <Grid display="flex" alignItems="center" justifyContent="space-between">
        <Text variant="h4" style={{ color: style.LIGHT.primary, flex: '1' }}>
          Users
        </Text>
        {isOriginalAreaAdmin() ||
          (isOriginalSuperAdmin() && (
            <FilterDropdown
              options={filterOptions}
              label="Filter By"
              filterBy={filterBy}
              setFilterBy={setFilterBy}
              gridSx={{ width: 450 }}
              selectSx={{ width: 170 }}
            />
          ))}
        <CustomButton
          add={true}
          type="submit"
          endIcon={<AddIcon />}
          onClick={() => setAddOpenModal(true)}>
          Add
        </CustomButton>
      </Grid>

      {showAlertBar && (
        <SuccessError
          successMessage={successMessage}
          errorMessage={errorMessage}
          setShowAlertBar={setShowAlertBar}
        />
      )}

      <Table data={getFilteredUsers()} options={tableOptionFilter} columns={computedColumns} />

      {showLoader && <Loader open={showLoader} />}

      {openDialog && (
        <DialogBox
          handleDialogBoxState={handleDialogState}
          openDialogBox={openDialog}
          handleConfirmAction={handleConfirm}
          deleteMessage={<DeleteSpan message="user associated with email" toBeDel={user.email} />}
        />
      )}

      {openModal && (
        <UpdateUser
          openModal={openModal}
          setOpenModal={setOpenModal}
          user={user}
          setSuccess={setSuccessMessage}
          setAlertBar={setShowAlertBar}
          setIsFormSaved={setIsFormSaved}
          isFormSaved={isFormSaved}
        />
      )}

      {addOpenModal && (
        <CreateUser
          addOpenModal={addOpenModal}
          setAddOpenModal={setAddOpenModal}
          setSuccess={setSuccessMessage}
          setAlertBar={setShowAlertBar}
          setIsFormSaved={setIsFormSaved}
          isFormSaved={isFormSaved}
        />
      )}
    </>
  );
};

export default Users;
